/**
 * @file Types relating to campaigns.
 *
 * TODO: consolidate with *modules/domains/campaigns*
 */
import { CampaignStateDefinition, ExperimentState } from 'modules/graphql/types';
import { CampaignType, EmbeddedDisplayUntil } from 'modules/domains/campaign/types';

export type CampaignId = string;

export interface LabelConfig {
  /** The name of the label */
  name?: string;
  /** The unique identifier of the label */
  id: number;
}

/** TODO: Move to constants */

export enum AttributionMode {
  AttributionPeriod = 'AttributionPeriod',
  LastMessaged = 'LastMessaged',
  ExplicitlySet = 'ExplicitlySet',
}

/** TODO: change to enum and move to constants */
export type DataScienceOptimization =
  | 'ChannelOptimization'
  | 'FrequencyOptimization'
  | 'SendTimeOptimization';

export interface SchedulerMsgParams {
  /** The timezone for the schedular */
  defaultTimeZone: string;
  startTimeZone: string;
  schedulerMsgIds: string[];
  /** Time range in which STO sends will go out */
  windowHours?: number;
  scheduleType?: string;
}

export interface CustomConversion {
  /** Name of event to be attributed */
  eventName?: string;
  /** Type of event attribution */
  attributionMode?: AttributionMode;
  /** Time range in which matching events will be attributed */
  attributionPeriodHours?: number;
  valueField?: string;
  dataScienceOptimizations?: DataScienceOptimization[];
}

export interface RawAttributionMode {
  name: AttributionMode;
  periodHours: number;
}

export interface RawConversionEvent {
  /** Name of event to be attributed */
  name: string;
  attributionMode: RawAttributionMode;
}

export interface RawConversion {
  events: RawConversionEvent[];
}

export interface Experiment {
  /** Id of experiment */
  id?: number;
  /** Name of experiment */
  name?: string;
  /** what is being experimented with */
  experimentType?: string;
  /** experiment state */
  state?: ExperimentState;
  /** experiment created at */
  createdAt?: string;
}

export type QuietHours = {
  startTime: string;
  endTime: string;
  sendSkip?: boolean;
} | null;

export type RateLimit = {
  rateLimitPerMin: number;
  useRateLimit: boolean;
} | null;

export interface Campaign {
  /** The name of the campaign */
  name?: string;
  /** Flag to override frequency cap if previously set on certain message mediums */
  ignoreFrequencyCap?: boolean;
  /** The unique identifier of the campaign */
  id?: number;
  /** Labels associated with the campaign */
  labels?: LabelConfig[];
  /** Custom event conversion data */
  customConversion?: CustomConversion | null;
  /** STO configuration */
  schedulerMsgParams?: SchedulerMsgParams | null;
  /** List of enabled data science optimizations (only STO currently supported) */
  dataScienceOptimizations?: DataScienceOptimization[];
  /** List of Experiments */
  experiments?: Experiment[];
  /** The priority level of the campaign */
  priorityLevel?: number;
  /**
   * List of event conversion data when `MultipleConversions` feature flag is
   * enabled and the REST API is being used.
   *
   * IMPORTANT: This ONLY returned/required by the rest API.  When using
   * graphql, use `conversions` instead.
   */
  customConversions?: CustomConversion[];
  /**
   * List of event conversion data when graphql is being used.
   *
   * IMPORTANT: This ONLY returned/required by the graphql.  When using the REST
   * API, use `customConversions` instead if the `MultipleConversions` feature
   * flag is enabled.
   */
  conversions?: RawConversion[];
  /** Quiet hours */
  customQuietHours?: QuietHours | null;
  /** Rate limit */
  customRateLimit?: RateLimit | null;
  /** The mobile apps associated with the campaign */
  mobileAppIds?: number[];
  /** The id of the associated template */
  templateId?: number;
  /** Placement ID needed for embedded campaigns */
  placementId?: number;
  /** Display priority order needed for embedded campaigns */

  embeddedPriorityOrder?: number;
  /** Display until configuration -- used in embedded campaigns */
  displayUntil?: EmbeddedDisplayUntil;
  /** Type of campaign */
  campaignTypeDefinition?: CampaignType;
  campaignState?: CampaignStateDefinition;
  campaignAlertsEnabled?: boolean;
  createdAt?: number;
  createdByUserId?: string;
  isArchived?: boolean;
  isDryRun?: boolean;
  labelIds?: number[];
  messageMedium?: string[];
  projectId?: number;
  quietHoursSendSkip?: boolean;
  teamId?: number;
  updatedAt?: number;
  updatedByUserId?: string;
  useCustomRateLimit?: boolean;
  wasEverRecurring?: boolean;
}

/** Data for backend calls */
export type GraphQLCampaign = Omit<Campaign, 'customConversions'>;
export type RestCampaign = Omit<Campaign, 'conversions' | 'experiments'>;
